/**
 * Cart Debug Logger - Enhanced debugging for cart state tracking
 *
 * This module provides comprehensive debugging capabilities for tracking
 * cart state changes, especially useful for diagnosing issues like:
 * - Quantity resets after navigation
 * - Duplicate cart entries
 * - State synchronization problems
 *
 * Usage:
 * 1. Enable debugging by setting: window.cartBFCacheDebug = true
 * 2. Or use the console helper: CartDebug.enable()
 * 3. View logs with: CartDebug.getLogs()
 * 4. Export logs with: CartDebug.exportLogs()
 *
 * @version 1.0.0
 * @date 2025-10-19
 */

class CartDebugLogger {
  constructor() {
    this.logs = [];
    this.maxLogs = 100; // Prevent memory leaks
    this.enabled = false;
    this.startTime = Date.now();
  }

  /**
   * Enable debug logging
   */
  enable() {
    this.enabled = true;
    window.cartBFCacheDebug = true;
    console.log('%c[Cart Debug] Debugging enabled', 'color: #00ff00; font-weight: bold');
    this.log('DEBUG_ENABLED', { timestamp: new Date().toISOString() });
  }

  /**
   * Disable debug logging
   */
  disable() {
    this.enabled = false;
    window.cartBFCacheDebug = false;
    console.log('%c[Cart Debug] Debugging disabled', 'color: #ff0000; font-weight: bold');
    this.log('DEBUG_DISABLED', { timestamp: new Date().toISOString() });
  }

  /**
   * Log a cart event
   * @param {string} eventType - Type of event (e.g., 'CART_LOADED', 'BFCACHE_RESTORE')
   * @param {object} data - Event data
   */
  log(eventType, data = {}) {
    const logEntry = {
      timestamp: Date.now() - this.startTime,
      datetime: new Date().toISOString(),
      eventType,
      data: JSON.parse(JSON.stringify(data)), // Deep clone to prevent mutations
      url: window.location.href,
      pathname: window.location.pathname
    };

    // Add to logs array
    this.logs.push(logEntry);

    // Trim logs if exceeding max
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Console output if enabled
    if (this.enabled || window.Shopify?.designMode) {
      const color = this.getEventColor(eventType);
      console.log(
        `%c[Cart Debug] ${eventType}`,
        `color: ${color}; font-weight: bold`,
        data
      );
    }
  }

  /**
   * Get color for event type
   * @param {string} eventType - Event type
   * @returns {string} CSS color
   */
  getEventColor(eventType) {
    const colors = {
      CART_LOADED: '#00ff00',
      CART_LOADING: '#ffaa00',
      CART_FAILED: '#ff0000',
      BFCACHE_RESTORE: '#00aaff',
      POPSTATE: '#aa00ff',
      QUANTITY_CHANGE: '#ffff00',
      DUPLICATE_ENTRY: '#ff00ff',
      STATE_MISMATCH: '#ff6600'
    };
    return colors[eventType] || '#888888';
  }

  /**
   * Log cart state snapshot
   * @param {object} cart - Cart object
   * @param {string} context - Context description
   */
  logCartState(cart, context = '') {
    if (!cart) return;

    const snapshot = {
      context,
      itemCount: cart.item_count || 0,
      totalPrice: cart.total_price || 0,
      items: (cart.items || []).map(item => ({
        key: item.key,
        id: item.id,
        variant_id: item.variant_id,
        title: item.product_title,
        quantity: item.quantity,
        price: item.price,
        final_line_price: item.final_line_price
      }))
    };

    this.log('CART_STATE_SNAPSHOT', snapshot);
  }

  /**
   * Detect and log duplicate cart entries
   * @param {object} cart - Cart object
   */
  detectDuplicates(cart) {
    if (!cart || !cart.items) return;

    const variantMap = new Map();
    const duplicates = [];

    cart.items.forEach((item, index) => {
      const variantId = item.variant_id;
      if (variantMap.has(variantId)) {
        duplicates.push({
          variant_id: variantId,
          title: item.product_title,
          entries: [
            { index: variantMap.get(variantId), key: cart.items[variantMap.get(variantId)].key },
            { index, key: item.key }
          ]
        });
      } else {
        variantMap.set(variantId, index);
      }
    });

    if (duplicates.length > 0) {
      this.log('DUPLICATE_ENTRY', { duplicates, totalItems: cart.items.length });
    }
  }

  /**
   * Compare two cart states and log differences
   * @param {object} oldCart - Previous cart state
   * @param {object} newCart - New cart state
   */
  compareCartStates(oldCart, newCart) {
    if (!oldCart || !newCart) return;

    const changes = {
      itemCountChange: newCart.item_count - oldCart.item_count,
      priceChange: newCart.total_price - oldCart.total_price,
      itemChanges: []
    };

    // Create maps for easier comparison
    const oldItems = new Map(oldCart.items.map(item => [item.key, item]));
    const newItems = new Map(newCart.items.map(item => [item.key, item]));

    // Check for changed or removed items
    oldItems.forEach((oldItem, key) => {
      const newItem = newItems.get(key);
      if (!newItem) {
        changes.itemChanges.push({
          type: 'REMOVED',
          key,
          title: oldItem.product_title,
          oldQuantity: oldItem.quantity
        });
      } else if (oldItem.quantity !== newItem.quantity) {
        changes.itemChanges.push({
          type: 'QUANTITY_CHANGED',
          key,
          title: oldItem.product_title,
          oldQuantity: oldItem.quantity,
          newQuantity: newItem.quantity,
          delta: newItem.quantity - oldItem.quantity
        });
      }
    });

    // Check for added items
    newItems.forEach((newItem, key) => {
      if (!oldItems.has(key)) {
        changes.itemChanges.push({
          type: 'ADDED',
          key,
          title: newItem.product_title,
          quantity: newItem.quantity
        });
      }
    });

    if (changes.itemChanges.length > 0 || changes.itemCountChange !== 0) {
      this.log('STATE_MISMATCH', changes);
    }
  }

  /**
   * Get all logs
   * @returns {array} Array of log entries
   */
  getLogs() {
    return this.logs;
  }

  /**
   * Get logs as formatted string
   * @returns {string} Formatted log string
   */
  getFormattedLogs() {
    return this.logs.map(log => {
      const timeStr = `[${log.datetime}] (+${log.timestamp}ms)`;
      const eventStr = log.eventType.padEnd(20);
      const dataStr = JSON.stringify(log.data);
      return `${timeStr} ${eventStr} ${dataStr}`;
    }).join('\n');
  }

  /**
   * Export logs to downloadable file
   */
  exportLogs() {
    const logsJson = JSON.stringify(this.logs, null, 2);
    const blob = new Blob([logsJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cart-debug-logs-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    console.log('%c[Cart Debug] Logs exported', 'color: #00ff00; font-weight: bold');
  }

  /**
   * Clear all logs
   */
  clear() {
    this.logs = [];
    console.log('%c[Cart Debug] Logs cleared', 'color: #ffaa00; font-weight: bold');
  }
}

// Create global singleton instance
const CartDebug = new CartDebugLogger();

// Make it globally accessible for console debugging
window.CartDebug = CartDebug;

// Export for module usage
export default CartDebug;
